import { useState } from "react";
import { ArrowDown, ArrowUp, Bike, ChevronRight, Phone } from "lucide-react";
import { Pill } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import {
  driverWhatsAppLink,
  partnerDriverPresence,
  vehicleLabel,
  type PartnerDriverPresence,
  type PartnerDriverRow,
} from "../../lib/partnerDrivers";
import { useI18n } from "../../i18n";

type SortKey =
  | "name"
  | "status"
  | "vehicle"
  | "orders"
  | "mode"
  | "pay"
  | "wallet";

const PRESENCE_RANK: Record<PartnerDriverPresence, number> = {
  online: 0,
  busy: 1,
  offline: 2,
};

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "DR"
  );
}

interface DriversTableProps {
  drivers: PartnerDriverRow[];
  loading?: boolean;
  /** Fleet wallet balances by driver id (v2 read model). */
  walletById: Map<string, number>;
  /** Partner default per-trip pay — shown when the driver has no override. */
  defaultPay: number | null;
  onOpen: (driver: PartnerDriverRow) => void;
  /** Toggle active state (off → confirm deactivate, on → activate). */
  onToggle: (driver: PartnerDriverRow) => void;
}

export function DriversTable({
  drivers,
  loading,
  walletById,
  defaultPay,
  onOpen,
  onToggle,
}: DriversTableProps) {
  const { t, locale } = useI18n();
  const [sortKey, setSortKey] = useState<SortKey>("status");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "name" || key === "status" ? "asc" : "desc");
  };

  const sortValue = (driver: PartnerDriverRow, key: SortKey): number | string => {
    switch (key) {
      case "name":
        return driver.name.toLowerCase();
      case "status":
        return PRESENCE_RANK[partnerDriverPresence(driver)];
      case "vehicle":
        return vehicleLabel(driver).toLowerCase();
      case "orders":
        return driver.active_orders;
      case "mode":
        return driver.driver_mode ?? "";
      case "pay":
        return driver.trip_fee_amount ?? defaultPay ?? -1;
      case "wallet":
        return walletById.get(driver.id) ?? 0;
    }
  };

  const sorted = [...drivers].sort((a, b) => {
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    const cmp =
      typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv)
        : Number(av) - Number(bv);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const columns: Array<{ key: SortKey; label: string; align?: "end" }> = [
    { key: "name", label: t("drivers.columns.name") },
    { key: "status", label: t("drivers.columns.status") },
    { key: "vehicle", label: t("drivers.columns.vehicle") },
    { key: "orders", label: t("drivers.columns.activeOrders"), align: "end" },
    { key: "mode", label: t("drivers.columns.mode") },
    { key: "pay", label: t("drivers.columns.pay"), align: "end" },
    { key: "wallet", label: t("drivers.columns.wallet"), align: "end" },
  ];

  const money = (value: number) => formatCurrency(value, "IQD", locale);

  return (
    <div className="report-table-wrap">
      <table className="table-flat">
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  className={col.align === "end" ? "text-end" : undefined}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className={`inline-flex items-center gap-1 uppercase tracking-[0.06em] ${
                      active ? "text-[var(--accent)]" : ""
                    }`}
                  >
                    {col.label}
                    {active ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )
                    ) : null}
                  </button>
                </th>
              );
            })}
            <th className="w-32" aria-label={t("drivers.title")} />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="py-10 text-center text-sm text-[var(--text-muted)]"
              >
                {t("common.loading")}
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="py-10 text-center">
                <Bike className="mx-auto h-7 w-7 text-[var(--text-muted)]" />
                <p className="mt-2 text-sm font-semibold text-[var(--text-secondary)]">
                  {t("drivers.emptyTitle")}
                </p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  {t("drivers.emptyHint")}
                </p>
              </td>
            </tr>
          ) : (
            sorted.map((driver) => {
              const presence = partnerDriverPresence(driver);
              const inactive = driver.is_active === false;
              const wallet = walletById.get(driver.id);
              const payAmount = driver.trip_fee_amount ?? defaultPay;
              const usingDefault =
                driver.trip_fee_amount == null && defaultPay != null;
              const waHref = driverWhatsAppLink(driver.phone);
              return (
                <tr
                  key={driver.id}
                  onClick={() => onOpen(driver)}
                  className={`cursor-pointer ${
                    inactive ? "drv-row--inactive" : ""
                  }`}
                >
                  <td>
                    <div className="flex items-center gap-2.5">
                      <span className="drv-avatar" aria-hidden>
                        {initialsOf(driver.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[var(--text-primary)]">
                          {driver.name}
                        </p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {driver.phone ? (
                            <span dir="ltr">{driver.phone}</span>
                          ) : (
                            t("common.dash")
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    {inactive ? (
                      <span className="drv-presence">
                        {t("drivers.inactive")}
                      </span>
                    ) : presence === "offline" ? (
                      <span className="drv-presence">
                        {t("drivers.presence.offline")}
                      </span>
                    ) : (
                      <Pill variant={presence === "online" ? "success" : "warning"}>
                        {t(
                          presence === "online"
                            ? "drivers.presence.online"
                            : "drivers.presence.busy"
                        )}
                      </Pill>
                    )}
                  </td>
                  <td className="text-sm text-[var(--text-secondary)]">
                    {vehicleLabel(driver)}
                  </td>
                  <td className="text-end">
                    <span className="tabular font-semibold">
                      {driver.active_orders}
                    </span>
                  </td>
                  <td className="text-sm text-[var(--text-secondary)]">
                    {driver.driver_mode
                      ? t(
                          driver.driver_mode === "paid"
                            ? "drivers.mode.paid"
                            : "drivers.mode.own"
                        )
                      : t("common.dash")}
                  </td>
                  <td className="text-end tabular text-sm">
                    {payAmount != null ? (
                      <>
                        {money(payAmount)}
                        {usingDefault ? (
                          <span className="ms-1 text-[10px] font-bold uppercase text-[var(--text-muted)]">
                            {t("drivers.payDefaultMark")}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      t("common.dash")
                    )}
                  </td>
                  <td className="text-end">
                    {wallet == null ? (
                      <span className="text-[var(--text-muted)]">
                        {t("common.dash")}
                      </span>
                    ) : (
                      <span
                        className={`tabular font-bold ${
                          wallet > 0
                            ? "text-[var(--accent)]"
                            : wallet < 0
                              ? "text-[var(--danger)]"
                              : ""
                        }`}
                      >
                        {money(wallet)}
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    <span className="drv-row-actions">
                      {waHref ? (
                        <a
                          className="drv-icon-action drv-icon-action--wa"
                          href={waHref}
                          target="_blank"
                          rel="noreferrer"
                          title={t("drivers.whatsapp")}
                          aria-label={t("drivers.whatsapp")}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="h-4 w-4" />
                        </a>
                      ) : null}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!inactive}
                        aria-label={
                          inactive ? t("drivers.activate") : t("drivers.deactivate")
                        }
                        title={
                          inactive ? t("drivers.activate") : t("drivers.deactivate")
                        }
                        className={`drv-switch ${inactive ? "" : "drv-switch--on"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(driver);
                        }}
                      >
                        <span className="drv-switch__knob" aria-hidden />
                      </button>
                      <ChevronRight className="inline h-4 w-4 text-[var(--text-muted)] rtl:rotate-180" />
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
